import { cloneDeep } from "lodash";
import moment from "moment";

import { Device } from "types";
import {
    NullableNumber,
    ReportFilterItem,
    ReportFilterItemsState,
    ReportIndividualDataSingle,
    ReportItemsState,
    ReportItemStateData,
    ReportItemSummaryType,
    ReportItemType,
    ReportPDFFilterItems,
    ReportRoom,
    RoomMetricValue,
} from "types/Reports";
import { formatValueBasedOnTypeInReport } from "./reportFormat";
import { initialReportFilterItemsState, initialReportItemsStateData } from "./reportInitData";
import { computeIndexByDayInMonth } from "./reportCompute";
import { getPSLDurationByUnit } from "service/proqueryService";

export const convertDevicesToFilterItems = (devices: Device[]): ReportFilterItem[] => {
    return devices.map((device) => {
        return {
            id: `${device.gatewayId}-${device.deviceId}`,
            name: device.sensorName,
            networkId: device.gatewayId,
        };
    });
};

export const convertSelectedDevicesToFilterItems = (devices: Device[], selectedIds: string[]): ReportFilterItem[] => {
    return convertDevicesToFilterItems(devices.filter((device) => selectedIds.includes(`${device.gatewayId}-${device.deviceId}`)));
};

export const convertSelectedIdsToDevices = (devices: Device[], selectedIds: string[]) => {
    return devices.filter((device) => selectedIds.includes(`${device.gatewayId}-${device.deviceId}`));
};

export const convertFilterItemsToDevices = (filterItems: ReportFilterItem[], devices: Device[]): Device[] => {
    const ids = new Set(filterItems.map((i) => i.id));

    return devices.filter((d) => ids.has(`${d.gatewayId}-${d.deviceId}`));
};

export const convertRoomsToFilterItems = (rooms: ReportRoom[]): ReportFilterItem[] => {
    return rooms.map((room) => {
        return {
            id: `${room.roomOrganizationId}-${room.roomUUId}`,
            name: room.roomName,
            networkId: room.gatewayId,
        };
    });
};

export const convertSelectedRoomsToFilterItems = (rooms: ReportRoom[], selectedIds: string[]): ReportFilterItem[] => {
    return convertRoomsToFilterItems(rooms.filter((room) => selectedIds.includes(`${room.roomOrganizationId}-${room.roomUUId}`)));
};

export const convertSelectedIdsToRooms = (rooms: ReportRoom[], selectedIds: string[]) => {
    return rooms.filter((room) => selectedIds.includes(`${room.roomOrganizationId}-${room.roomUUId}`));
};

export const convertFilterItemsToRooms = (filterItems: ReportFilterItem[], rooms: ReportRoom[]): ReportRoom[] => {
    const ids = new Set(filterItems.map((i) => i.id));

    return rooms.filter((room) => ids.has(`${room.roomOrganizationId}-${room.roomUUId}`));
};

export const convertRoomsToIds = (rooms: ReportRoom[]) => {
    return rooms.map((room) => `${room.roomOrganizationId}-${room.roomUUId}`);
};

export const convertDevicesToIds = (devices: Device[]) => {
    return devices.map((device) => `${device.gatewayId}-${device.deviceId}`);
};

export const convertFilterItemsToIds = (filterItems: ReportFilterItem[]) => {
    return filterItems.map((i) => i.id);
};

export const convertReportItemsStateToReportFilterItemsState = (items: Readonly<ReportItemsState>): ReportFilterItemsState => {
    const result: ReportFilterItemsState = cloneDeep(initialReportFilterItemsState);
    const types = (Object.keys(items) as (keyof ReportFilterItemsState)[]);
    for (const type of types) {
        if (type === 'room') {
            result.room.all = convertRoomsToFilterItems(items.room.all);
            result.room.selected = convertSelectedRoomsToFilterItems(items.room.all, items.room.selectedIds);
        } else {
            result[type].all = convertDevicesToFilterItems(items[type].all);
            result[type].selected = convertSelectedDevicesToFilterItems(items[type].all, items[type].selectedIds);
        }
    }

    return result;
};

export const convertReportIndividualItemsToRows = (
    data: ReportIndividualDataSingle[],
    dateFormat: string,
    itemType: ReportItemSummaryType,
): string[][] => {
    return data.map((item) => {
        const date = item.date === null ? '--' : item.date.format(dateFormat);
        // format value for the horizontal table chart
        const value = formatValueBasedOnTypeInReport(item.value, itemType, false);
        const average = formatValueBasedOnTypeInReport(item.average, itemType, false);

        return [item.name, value, date, average];
    });
};

// Built from selectedIds (not appliedIds) because handleApply seeds placeholders
// before its setItems dispatch has flushed — selectedIds already holds what the
// user is applying. Id formats here mirror the real retriever's outputs so React
// keys stay stable across the placeholder -> real-data swap:
//   room          -> room.roomUUId                (createItemSeriesFromRoomUsageData)
//   energy/water  -> `${gatewayId}_${deviceId}_${said}` (computeReportChartSeries)
//   sensors + waterLeakage -> `${gatewayId}-${deviceId}` (caller of createItemSeriesFromRawData)
export const buildLoadingPlaceholderItemsData = (items: Readonly<ReportItemsState>): ReportItemStateData => {
    const result: ReportItemStateData = cloneDeep(initialReportItemsStateData);

    const appliedRooms = convertSelectedIdsToRooms(items.room.all, items.room.selectedIds);
    result.room.chartSeries = appliedRooms.map((room) => ({
        id: room.roomUUId,
        name: room.roomName,
        data: [],
        weekdaysData: [],
        weekendsData: [],
    }));

    const meterTypes = ['energy', 'water'] as const;
    for (const type of meterTypes) {
        const devices = convertSelectedIdsToDevices(items[type].all, items[type].selectedIds);
        result[type].chartSeries = devices.map((d) => ({
            id: `${d.gatewayId}_${d.deviceId}_${d.said}`,
            name: d.sensorName,
            data: [],
            weekdaysData: [],
            weekendsData: [],
        }));
    }

    const sensorTypes = [
        'waterLeakage', 'temperature', 'humidity', 'co2', 'pressure',
        'tvoc', 'pm1', 'pm2.5', 'pm10',
    ] as const;
    for (const type of sensorTypes) {
        const devices = convertSelectedIdsToDevices(items[type].all, items[type].selectedIds);
        result[type].chartSeries = devices.map((d) => ({
            id: `${d.gatewayId}-${d.deviceId}`,
            name: d.sensorName,
            data: [],
            weekdaysData: [],
            weekendsData: [],
        }));
    }

    return result;
};

export const convertReportItemsStateToPDFFilterItems = (items: ReportItemsState) => {
    const filterItems: ReportPDFFilterItems = {
        room: convertSelectedRoomsToFilterItems(items.room.all, items.room.appliedIds),
        energy: convertSelectedDevicesToFilterItems(items.energy.all, items.energy.appliedIds),
        water: convertSelectedDevicesToFilterItems(items.water.all, items.water.appliedIds),
        waterLeakage: convertSelectedDevicesToFilterItems(items.waterLeakage.all, items.waterLeakage.appliedIds),
        temperature: convertSelectedDevicesToFilterItems(items.temperature.all, items.temperature.appliedIds),
        humidity: convertSelectedDevicesToFilterItems(items.humidity.all, items.humidity.appliedIds),
        co2: convertSelectedDevicesToFilterItems(items.co2.all, items.co2.appliedIds),
        pressure: convertSelectedDevicesToFilterItems(items.pressure.all, items.pressure.appliedIds),
        tvoc: convertSelectedDevicesToFilterItems(items.tvoc.all, items.tvoc.appliedIds),
        pm1: convertSelectedDevicesToFilterItems(items.pm1.all, items.pm1.appliedIds),
        'pm2.5': convertSelectedDevicesToFilterItems(items["pm2.5"].all, items["pm2.5"].appliedIds),
        pm10: convertSelectedDevicesToFilterItems(items.pm10.all, items.pm10.appliedIds),
    };

    return filterItems
};

export const convertSeriesDataToAdaptMinBarHeight = (itemType: ReportItemType, data: NullableNumber[]) => {
    return data.map((value) => {
        if (value === 0) {
            return {
                value: null,
                originalValue: value,
            };
        }

        const valueDisplay = formatValueBasedOnTypeInReport(value, itemType, false);

        if (
            Number(valueDisplay) === 0 ||
            valueDisplay === '-' ||
            valueDisplay === '0 h 0 m'
        ) {
            return {
                value: null,
                originalValue: value,
            };
        }

        return {
            value,
            originalValue: value,
        };
    });
};

export const convertRoomMetricValuesToData = (
    values: RoomMetricValue[],
    categories: string[],
) => {
    const result: NullableNumber[] = categories.map(() => null);

    for (const value of values) {
        const date = moment.unix(value[0]);
        const idx = computeIndexByDayInMonth(categories, date.date());

        if (idx === null) {
            continue;
        }

        result[idx] = getPSLDurationByUnit(Number(value[1]), "hours");
    }

    return result;
};
